import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import clientsService from '../services/clientsService';
import s3Service from '../services/s3Service';
import {
  validateDocumentFile,
  getDocumentAcceptValue,
} from '../utils/documentValidation';
import styles from '../assets/propertyDocumentEdit.module.css'; // Reuse property document styles

const ClientDocumentEdit = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [document, setDocument] = useState(null);
  const [client, setClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    document_type_id: '',
    visible_to_client: false,
    document_key: null,
    document_name: null,
    document_type: null,
    document_size: null,
  });
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadDocumentData();
  }, [documentId]);

  const loadDocumentData = async () => {
    try {
      setLoading(true);
      const response = await clientsService.editClientDocument(documentId);
      
      if (response.success) {
        const { document: doc, client: cli } = response.data;
        setDocument(doc);
        setClient(cli);
        
        // Set form data
        setFormData({
          name: doc.name || '',
          document_type_id: doc.document_type_id || '',
          visible_to_client: doc.visible_to_client || false,
          document_key: doc.file || null,
          document_name: doc.file ? doc.file.split('/').pop() : null,
          document_type: null,
          document_size: doc.size || null,
        });
      } else {
        toast.error(response.message || 'Failed to load document data');
        if (client) {
          navigate(`/clients/${client.id}`);
        } else {
          navigate('/clients');
        }
      }
    } catch (error) {
      console.error('Error loading document data:', error);
      toast.error('Failed to load document data');
      if (client) {
        navigate(`/clients/${client.id}`);
      } else {
        navigate('/clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        document_key: null,
        document_name: null,
        document_type: null,
        document_size: null,
      }));
      setUploadProgress(0);
      return;
    }

    // Validate file
    const validationResult = validateDocumentFile(file);
    if (!validationResult.isValid) {
      toast.error(validationResult.error);
      e.target.value = "";
      return;
    }

    // Start upload process
    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload file to S3
      // Laravel S3 path structure: client/{collection-slug}/{media-id}/{filename}
      // Matching Laravel MediaPathResolver: client/{collection-slug}/{media-id}/{filename}
      
      // Get document type name and slug it (matching Laravel str_slug)
      // Document types mapping (matching Laravel config/client.php)
      const CLIENT_DOCUMENT_TYPES = {
        1: 'Completed Client Objection Handling Form',
        2: 'Completed Proposal',
        3: 'Negotiation Form',
        4: 'Completed Client Onboarding Form',
        5: 'Meeting Notes',
        6: 'Empathy Map',
        7: 'Tender Document',
        8: 'Company Strategy/Marketing Material',
        9: 'Finance Report',
        10: 'Miscellaneous',
      };
      
      const documentTypeName = CLIENT_DOCUMENT_TYPES[formData.document_type_id] || `Document Type ${formData.document_type_id}`;
      const collectionSlug = documentTypeName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      // Use document ID as media ID (or timestamp if document not loaded yet)
      const mediaId = document?.id || Date.now();
      
      // Laravel-compatible path structure with private disk prefix
      // Laravel uses 'private' disk (useDisk('private')) which adds 'private/' prefix in S3
      const folder = `private/client/${collectionSlug}/${mediaId}`;
      
      const s3Key = await s3Service.uploadDocument(file, {
        folder: folder,
        useLaravelStructure: true, // Use Laravel path structure (no timestamp prefix)
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      // Update form data with S3 information
      setFormData((prev) => ({
        ...prev,
        document_key: s3Key,
        document_name: file.name,
        document_type: file.type,
        document_size: file.size,
      }));

      toast.success("Document uploaded successfully!");
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Failed to upload document");
      e.target.value = "";
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.document_type_id) {
      newErrors.document_type_id = 'Document type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      const submitData = {
        name: formData.name,
        visible_to_client: formData.visible_to_client ? 1 : 0,
      };

      // Include new document key if file was uploaded
      if (formData.document_key) {
        submitData.document_key = formData.document_key;
      }

      const response = await clientsService.updateClientDocument(
        documentId, 
        submitData
      );
      
      if (response.success) {
        toast.success('Document updated successfully');
        navigate(`/clients/${client.id}`);
      } else {
        toast.error(response.message || 'Failed to update document');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (client) {
      navigate(`/clients/${client.id}`);
    } else {
      navigate('/clients');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <div className="text-center">
              <p>Loading document data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!document || !client) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <div className="text-center">
              <p>Document not found</p>
              <button className="btn btn-primary" onClick={handleCancel}>
                Back to Client
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <ol className="breadcrumb">
        <li><a href="/">Dashboard</a></li>
        <li><a href="/clients">Clients</a></li>
        <li><a href={`/clients/${client.id}`}>{client.name}</a></li>
        <li className="active">Edit document</li>
      </ol>

      {/* Main Content */}
      <div className="panel">
        <div className="panel-body">
          <h1 className={styles.pageTitle}>Edit client document</h1>
          
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className="row">
              <div className="col-md-6">
                <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
                  <label className="control-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter document name"
                  />
                  {errors.name && (
                    <span className="help-block text-danger">{errors.name}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className={`form-group ${errors.document_type_id ? 'has-error' : ''}`}>
                  <label className="control-label">
                    Document Type <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={document.document_type || 'Unknown'}
                    className="form-control"
                    disabled
                    readOnly
                  />
                  <input
                    type="hidden"
                    name="document_type_id"
                    value={formData.document_type_id}
                  />
                  {errors.document_type_id && (
                    <span className="help-block text-danger">{errors.document_type_id}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Document</label>
                  <div>
                    {formData.document_name && !uploading && (
                      <div className="mb-2">
                        <span>Current: {formData.document_name}</span>
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="form-control"
                      accept={getDocumentAcceptValue()}
                      disabled={uploading}
                    />
                    {uploading && (
                      <div className="mt-2">
                        <div className="progress">
                          <div 
                            className="progress-bar"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <span>Uploading... {Math.round(uploadProgress)}%</span>
                      </div>
                    )}
                    <small className="form-text text-muted">
                      Leave empty to keep current document, or upload a new one to replace it.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="visible_to_client"
                        checked={formData.visible_to_client}
                        onChange={handleInputChange}
                      />
                      Visible to client
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-default"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save document'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientDocumentEdit;

